import { useCallback, useState } from 'react';
import type { ReactElement } from 'react';
import { Tab, TabList } from '@simplywallst/ui-core';
import type { TabOnClickEvent } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
import { Wrapper, PeriodTabListWrapper, HeadCell, DataTable } from './styled';
import SectorsPerformanceSkeleton from './SectorsPerformaceSkeleton';
import Tooltip from '@/components/Tooltip';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import { ToolTipText } from '../MarketPerformance/styled';

enum SectorTab {
  marketCap = 'Returns',
  pe = 'Valuation',
}

enum Period {
  '7D' = '7D',
  '1M' = '1M',
  '3M' = '3M',
  '1Y' = '1Y',
  '3Y' = '3Y',
  '5Y' = '5Y',
}

export type PeriodType = keyof typeof Period;

export type SectorTabType = keyof typeof SectorTab;

interface ChildrenProps {
  tab: SectorTabType;
  period: Period;
}

interface Props {
  disabled: boolean;
  isShowingSecondTabHeader: boolean;
  children: (
    props: ChildrenProps
  ) => ReactElement<ChildrenProps>[] | ReactElement<ChildrenProps>;
  isSector: boolean;
}

export const SectorPerformance = ({
  disabled,
  children,
  isShowingSecondTabHeader,
  isSector,
}: Props) => {
  const { trackEvent } = useTracking();
  const [activeTab, setActiveTab] = useState<SectorTabType>('marketCap');
  const [activePeriod, setActivePeriod] = useState<Period>(Period['7D']);
  const handleTabSelect = useCallback(
    (event: TabOnClickEvent<SectorTabType>) => {
      const value = event.target.value;
      setActiveTab(value);
      trackEvent({
        type: 'track',
        action: 'click',
        subject: 'sector-trends-tab',
        properties: value,
      });
    },
    []
  );
  const handlePeriodSelect = useCallback((event: TabOnClickEvent<Period>) => {
    const value = event.target.value;
    trackEvent({
      type: 'track',
      action: 'click',
      subject: `sectors-performance-${value}`,
    });
    setActivePeriod(value);
  }, []);
  const trackCallback = (term: string) => {
    trackEvent({
      type: 'track',
      action: 'hover',
      subject: 'sector-trends-valuation-header',
      properties: term,
    });
  };
  const getTerm = useGlossaryTerms();
  const getGlossaryWithTooltip = (
    term: string,
    index: number,
    trackCallback: (string) => void
  ) => {
    const matchedGlossary = getTerm(term.toLowerCase())?.replace(
      '{sector/industry}',
      isSector ? 'sector' : 'industry'
    );
    return (
      <Tooltip
        key={index}
        title={<ToolTipText>{matchedGlossary}</ToolTipText>}
        onOpen={() => trackCallback(term)}
        light
        zIndex={99}
      >
        <span>{term}</span>
      </Tooltip>
    );
  };

  return (
    <Wrapper>
      <TabList templateColumns="repeat(2, max-content)">
        <Tab
          data-cy-id="sector-performance-returns-tab"
          selected={activeTab === 'marketCap'}
          onClick={handleTabSelect}
          value="marketCap"
          disabled={disabled}
        >
          {SectorTab.marketCap}
        </Tab>
        <Tab
          data-cy-id="sector-performance-valuation-tab"
          selected={activeTab === 'pe'}
          onClick={handleTabSelect}
          value="pe"
          disabled={disabled}
        >
          {SectorTab.pe}
        </Tab>
      </TabList>
      <DataTable
        columnWidths={activeTab === 'pe' ? '4, 1, 3.5, 1.5, 0' : '4, 1, 5'}
        columnTextAlignments={activeTab === 'pe' ? 'l,r,l,r,l' : 'l,r,l,l'}
      >
        <thead>
          <tr>
            {activeTab === 'pe' ? (
              isShowingSecondTabHeader && (
                <>
                  <th />
                  <HeadCell colSpan={2}>
                    {getGlossaryWithTooltip(
                      'Price to Earnings',
                      1,
                      trackCallback
                    )}
                  </HeadCell>
                  <HeadCell colSpan={2}>
                    {getGlossaryWithTooltip(
                      'Forecasted Growth',
                      1,
                      trackCallback
                    )}
                  </HeadCell>
                </>
              )
            ) : (
              <th colSpan={3}>
                <PeriodTabListWrapper>
                  <TabList
                    templateColumns={`repeat(${
                      Object.keys(Period).length
                    }, auto)`}
                  >
                    {Object.keys(Period).map((p) => (
                      <Tab
                        key={p}
                        styleType="period"
                        selected={activePeriod === p}
                        onClick={handlePeriodSelect}
                        value={Period[p]}
                        name={`sectors-performance-${p}`}
                        disabled={disabled}
                      >
                        {p}
                      </Tab>
                    ))}
                  </TabList>
                </PeriodTabListWrapper>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {disabled ? (
            <SectorsPerformanceSkeleton
              row={10}
              column={activeTab === 'pe' ? 5 : 4}
            />
          ) : (
            children({
              tab: activeTab,
              period: activePeriod,
            })
          )}
        </tbody>
      </DataTable>
    </Wrapper>
  );
};

export default SectorPerformance;
